/**
 * MinistrareIA - Chat Window Component (SSE Optimized + AI Workspace v3)
 * WhatsApp-style message display with real-time SSE updates
 * 
 * AI WORKSPACE WITH FILE ATTACHMENTS:
 * - Supports artifacts/files from AI responses
 * - Files are attached when using "Usar resposta"
 * - Shows file preview in input area
 * - Sends text + files via sendMessageWithMedia endpoint
 * 
 * ✅ FIX: History now uses integrated useAIWorkspace hook
 * ✅ FIX: Now uses sendMessageWithMedia for text + files
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send,
  Paperclip,
  MoreVertical,
  User,
  Users,
  Loader2,
  Image as ImageIcon,
  FileText,
  ArrowDown,
  Check,
  CheckCheck,
  Bot,
  ShieldOff,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
import whatsappService from '../../services/wa-personal/whatsappService';
import chatUtils from '../../utils/wa-personal/chat_utils';

// AI Workspace imports
import { 
  AIWorkspace, 
  MessageCheckbox, 
  AttachedFilesPreview,
  useAIWorkspace,
  getWorkspaceColors,
  BLOCKED_MESSAGE_TYPES,
  MAX_SELECTED_MESSAGES,
  ACTION_TYPES,
} from '../../components/AIWorkspace';
import aiAgentService from '../../services/global/aiAgentService';
import fileUploadService from '../../services/global/fileUploadService';
import MessageMedia from '../../components/MessageMedia';

const ChatWindow = ({ chat, onMessageSent, autopilotContacts, onToggleAutopilot, blockedContacts, onToggleBlockedContact }) => {
  const { colors, theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  
  // Attached files state (from AI Workspace)
  const [attachedFiles, setAttachedFiles] = useState([]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const currentChatRef = useRef(null);
  const fileInputRef = useRef(null);

  // ✅ AI Workspace hook (with integrated history)
  const aiWorkspace = useAIWorkspace();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  
  // File upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Keep track of current chat for SSE callbacks
  useEffect(() => {
    currentChatRef.current = chat;
  }, [chat]);

  // Reset workspace and attached files when chat changes
  useEffect(() => {
    aiWorkspace.resetWorkspace();
    setNewMessage('');
    setAttachedFiles([]);
  }, [chat?.remoteJid]);

  // ============================================
  // SSE EVENT HANDLERS
  // ============================================

  useSSEEvent(SSE_EVENTS.NEW_MESSAGE, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;

    const messageJid = data.remoteJid;
    const altJid = data.key?.remoteJidAlt;
    
    const isCurrentChat = (
      messageJid === currentChat.remoteJid ||
      messageJid === currentChat._altJid ||
      altJid === currentChat.remoteJid ||
      altJid === currentChat._altJid ||
      currentChat._phoneNumber === messageJid?.split('@')[0]
    );

    if (!isCurrentChat) return;

    const newMsg = {
      id: data.messageId || `sse-${Date.now()}`,
      key: data.key || {
        id: data.messageId,
        fromMe: data.fromMe,
        remoteJid: data.remoteJid,
      },
      message: {
        conversation: data.textContent,
      },
      messageType: data.messageType,
      messageTimestamp: data.timestamp || Math.floor(Date.now() / 1000),
      pushName: data.pushName,
      status: data.fromMe ? 'sent' : 'received',
    };

    setMessages(prev => {
      const exists = prev.some(m => 
        m.key?.id === newMsg.key?.id || 
        m.id === newMsg.id
      );
      
      if (exists) return prev;
      
      const updated = [...prev, newMsg];
      setTimeout(() => scrollToBottom(), 100);
      return updated;
    });

    if (!data.fromMe && data.messageId) {
      whatsappService.markAsRead(currentChat.remoteJid, data.messageId).catch(() => {});
    }
  }, []);

  useSSEEvent(SSE_EVENTS.MESSAGE_STATUS, (data) => {
    const currentChat = currentChatRef.current;
    if (!currentChat || !data) return;

    const messageJid = data.remoteJid;
    const isCurrentChat = (
      messageJid === currentChat.remoteJid ||
      messageJid === currentChat._altJid ||
      currentChat._phoneNumber === messageJid?.split('@')[0]
    );

    if (!isCurrentChat) return;

    setMessages(prev => prev.map(msg => {
      if (msg.key?.id === data.messageId) {
        return { ...msg, status: data.status };
      }
      return msg;
    }));
  }, []);

  // ============================================
  // LOAD MESSAGES
  // ============================================

  const loadMessages = useCallback(async () => {
    if (!chat?.remoteJid) return;

    setLoading(true);

    try {
      const response = await whatsappService.getChatMessages(chat.remoteJid, 100);
      let loadedMessages = response.data || [];
      
      loadedMessages = [...loadedMessages].sort((a, b) => {
        const timestampA = Number(a.messageTimestamp) || 0;
        const timestampB = Number(b.messageTimestamp) || 0;
        return timestampA - timestampB;
      });

      setMessages(loadedMessages);
      
      const lastReceivedMessage = [...loadedMessages]
        .reverse()
        .find(msg => msg.key && !msg.key.fromMe);
      
      if (lastReceivedMessage?.key?.id) {
        whatsappService.markAsRead(chat.remoteJid, lastReceivedMessage.key.id).catch(() => {});
      }

      setTimeout(() => scrollToBottom(), 100);

    } catch (err) {
      console.error('Error loading messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [chat?.remoteJid]);

  useEffect(() => {
    if (chat?.remoteJid) {
      loadMessages();
    } else {
      setMessages([]);
    }
  }, [chat?.remoteJid, loadMessages]);

  useEffect(() => {
    if (chat && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chat]);

  // ============================================
  // HELPERS
  // ============================================

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
  };

  // ============================================
  // SEND MESSAGE (with files support)
  // ============================================
  // ✅ OPTIMISTIC UI: Message appears immediately, input is freed instantly
  // API call runs in background - errors silently remove the message

  const handleSend = async (e, textOverride = null) => {
    e?.preventDefault();
    const messageText = textOverride || newMessage.trim();
    
    // Allow send if has text OR has files
    const hasText = messageText && messageText.length > 0;
    const hasFiles = attachedFiles.length > 0;
    
    if (!hasText && !hasFiles) return;
    if (sending) return;

    // Capture files before clearing
    const filesToSend = [...attachedFiles];
    
    // ✅ OPTIMISTIC: Clear input and files IMMEDIATELY
    if (!textOverride) {
      setNewMessage('');
    }
    setAttachedFiles([]);
    
    // ✅ Brief lock to prevent double-click, then release
    setSending(true);
    setTimeout(() => setSending(false), 300);

    // Create temp message for optimistic UI
    const tempId = `temp-${Date.now()}`;
    
    // Build proper message structure for media rendering
    let tempMessageContent = {};
    
    if (hasFiles && filesToSend.length > 0) {
      const firstFile = filesToSend[0];
      const fileType = firstFile.type || 'document';
      
      if (fileType === 'image') {
        tempMessageContent = {
          imageMessage: {
            caption: messageText || null,
            url: firstFile.url,
          }
        };
      } else if (fileType === 'video') {
        tempMessageContent = {
          videoMessage: {
            caption: messageText || null,
            url: firstFile.url,
          }
        };
      } else if (fileType === 'audio') {
        tempMessageContent = {
          audioMessage: {
            url: firstFile.url,
            seconds: 0,
          }
        };
      } else {
        tempMessageContent = {
          documentMessage: {
            fileName: firstFile.filename || 'Documento',
            mimetype: firstFile.mimeType || 'application/octet-stream',
            url: firstFile.url,
          }
        };
      }
      
      // Multiple files indicator
      if (filesToSend.length > 1) {
        tempMessageContent.conversation = messageText || `📎 +${filesToSend.length - 1} arquivo(s)`;
      }
    } else if (hasText) {
      tempMessageContent = { conversation: messageText };
    }
    
    const tempMessage = {
      id: tempId,
      key: { fromMe: true, id: tempId, remoteJid: chat.remoteJid },
      message: tempMessageContent,
      messageTimestamp: Math.floor(Date.now() / 1000),
      status: 'pending',
      _isTemp: true,
      _attachedFiles: filesToSend,
    };
    
    // ✅ Add message to list IMMEDIATELY
    setMessages(prev => [...prev, tempMessage]);
    scrollToBottom();

    // ✅ API call runs in BACKGROUND - doesn't block UI
    try {
      const number = whatsappService.extractNumber(chat.remoteJid);
      
      console.log('[ChatWindow] Sending (background):', {
        number,
        textLength: messageText?.length || 0,
        filesCount: filesToSend.length,
      });
      
      let result;
      
      if (hasFiles) {
        result = await whatsappService.sendMessageWithMedia(
          number,
          messageText || null,
          filesToSend.map(f => ({
            url: f.url,
            type: f.type || 'document',
            filename: f.filename,
            mimeType: f.mimeType || null,
          }))
        );
        
        if (result.data?.partial) {
          console.warn('[ChatWindow] Partial success:', result.data.errors);
        }
        
        // Update with real ID (optional, improves status tracking)
        const realId = result.data?.text_message_id || 
                       result.data?.file_results?.[0]?.message_id;
        
        if (realId) {
          setMessages(prev => prev.map(m => 
            m.id === tempId 
              ? { ...m, id: realId, key: { ...m.key, id: realId }, status: 'sent', _isTemp: false }
              : m
          ));
        }
      } else {
        result = await whatsappService.sendMessage(number, messageText);
        
        const realId = result.data?.message_id;
        if (realId) {
          setMessages(prev => prev.map(m => 
            m.id === tempId 
              ? { ...m, id: realId, key: { ...m.key, id: realId }, status: 'sent', _isTemp: false }
              : m
          ));
        }
      }
      
      onMessageSent?.({ text: messageText, files: filesToSend });
      
    } catch (err) {
      // ✅ SILENT ERROR: Remove message quietly, don't restore input
      console.error('[ChatWindow] Send failed (silent):', err.message);
      setMessages(prev => prev.filter(m => m.id !== tempId));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Hoje';
    if (date.toDateString() === yesterday.toDateString()) return 'Ontem';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const getMessageContent = (msg) => {
    const message = msg.message || {};
    
    if (message.reactionMessage) {
      return `${message.reactionMessage.text || '👍'} Reagiu a uma mensagem`;
    }
    
    return (
      message.conversation ||
      message.extendedTextMessage?.text ||
      message.imageMessage?.caption ||
      message.videoMessage?.caption ||
      message.documentMessage?.fileName ||
      (message.imageMessage ? '📷 Imagem' : '') ||
      (message.videoMessage ? '🎥 Vídeo' : '') ||
      (message.audioMessage ? '🎵 Áudio' : '') ||
      (message.stickerMessage ? '🎨 Sticker' : '') ||
      (message.documentMessage ? '📄 Documento' : '') ||
      (message.contactMessage ? '👤 Contato' : '') ||
      (message.locationMessage ? '📍 Localização' : '') ||
      '[Mensagem]'
    );
  };

  const getMessageText = (msg) => {
    const message = msg.message || {};
    return (
      message.conversation ||
      message.extendedTextMessage?.text ||
      ''
    );
  };

  const isMessageBlocked = (msg) => {
    const message = msg.message || {};
    return BLOCKED_MESSAGE_TYPES.some(type => message[type]);
  };

  // Check if message has media content
  const hasMediaContent = (msg) => {
    const message = msg.message || {};
    return !!(
      message.imageMessage ||
      message.videoMessage ||
      message.audioMessage ||
      message.documentMessage
    );
  };

  // Check if message is text-only (no media)
  const isTextOnly = (msg) => {
    const message = msg.message || {};
    return !!(
      message.conversation ||
      message.extendedTextMessage?.text
    ) && !hasMediaContent(msg);
  };

  const getMessageStatusIcon = (status) => {
    const iconStyle = { marginLeft: 4 };
    
    switch (status) {
      case 'read':
      case 'READ':
        return <CheckCheck size={14} style={{ ...iconStyle, color: '#53bdeb' }} />;
      case 'delivered':
      case 'DELIVERY_ACK':
        return <CheckCheck size={14} style={{ ...iconStyle, color: colors.messageTimeSent }} />;
      case 'sent':
      case 'SERVER_ACK':
        return <Check size={14} style={{ ...iconStyle, color: colors.messageTimeSent }} />;
      case 'pending':
        return <Loader2 size={12} style={{ ...iconStyle, color: colors.messageTimeSent, animation: 'spin 1s linear infinite' }} />;
      default:
        return null;
    }
  };

  const getChatName = () => {
    return chat?.pushName || chat?.name || chatUtils.formatPhoneNumber(chat?._phoneNumber) || whatsappService.extractNumber(chat?.remoteJid);
  };

  const getChatAvatar = () => {
    if (chat?.profilePicUrl) {
      return <img src={chat.profilePicUrl} alt="" style={styles.avatarImg} />;
    }
    
    const isGroup = chat?._isGroup || whatsappService.isGroup(chat?.remoteJid);
    
    return (
      <div style={styles.avatarPlaceholder}>
        {isGroup ? <Users size={24} /> : <User size={24} />}
      </div>
    );
  };

  // ============================================
  // ATTACHED FILES HANDLERS
  // ============================================

  const handleRemoveAttachedFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearAttachedFiles = () => {
    setAttachedFiles([]);
  };

  // ============================================
  // MEDIA DOWNLOAD HANDLER
  // ============================================

  const handleDownloadMedia = useCallback(async (messageKey, mediaType) => {
    try {
      console.log('[ChatWindow] Downloading media:', messageKey, mediaType);
      const result = await whatsappService.downloadMediaFromMessage(messageKey, mediaType);
      return result;
    } catch (error) {
      console.error('[ChatWindow] Media download failed:', error);
      throw error;
    }
  }, []);

  // ============================================
  // FILE UPLOAD HANDLERS
  // ============================================

  // Trigger file input click
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection from input
  const handleFileSelect = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    // ✅ Create a copy of files BEFORE clearing input
    // FileList is a reference to the input, clearing it loses the files
    const files = Array.from(fileList);
    
    // Clear the input so the same file can be selected again
    e.target.value = '';

    setUploading(true);
    setUploadError(null);

    try {
      console.log('[ChatWindow] Uploading files:', files.length);

      // Upload files (now passing array, not FileList)
      const results = await fileUploadService.uploadMultiple(files, (progress) => {
        console.log('[ChatWindow] Upload progress:', progress);
      });

      // Add uploaded files to attached files
      if (results.uploaded.length > 0) {
        setAttachedFiles(prev => [...prev, ...results.uploaded]);
        console.log('[ChatWindow] Files uploaded:', results.uploaded.length);
      }

      // Show errors if any
      if (results.errors.length > 0) {
        const errorMsg = results.errors.map(e => `${e.filename}: ${e.error}`).join('\n');
        setUploadError(errorMsg);
        console.error('[ChatWindow] Upload errors:', results.errors);
        
        // Clear error after 5 seconds
        setTimeout(() => setUploadError(null), 5000);
      }

    } catch (error) {
      console.error('[ChatWindow] Upload failed:', error);
      setUploadError(error.message);
      
      // Clear error after 5 seconds
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // AI WORKSPACE HANDLERS
  // ============================================

  // Execute TRANSLATE (uses input text)
  // Execute TRANSLATE (uses input text)
  // ✅ Now stores originalText for regeneration
  const handleExecuteTranslate = async () => {
    if (!newMessage.trim()) return;

    const originalText = newMessage.trim();

    await aiWorkspace.executeTranslate(originalText, async ({ action, text, languages }) => {
      const results = await aiAgentService.processAction({
        action,
        text,
        languages,
      });
      
      // ✅ Store originalText in each result for regeneration
      return results.map(r => ({
        ...r,
        originalText: originalText,
      }));
    });
  };

  // Execute GENERATE (uses selected messages)
  // ✅ Now stores originalMessages for regeneration
  const handleExecuteGenerate = async () => {
    const selectedMsgs = messages.filter(msg => {
      const msgId = msg.id || msg.key?.id;
      return aiWorkspace.selectedMessages.has(msgId);
    });
    
    const messageTexts = selectedMsgs.map(getMessageText).filter(Boolean);
    
    if (messageTexts.length === 0) return;

    // Store messages for regeneration
    const contextWithMessages = {
      chatId: chat?.remoteJid,
      chatName: getChatName(),
      originalMessages: messageTexts, // ✅ Store for regeneration
    };

    await aiWorkspace.executeGenerate(messageTexts, async ({ action, messages: msgs }) => {
      const results = await aiAgentService.processAction({
        action,
        messages: msgs,
        languages: [], // Empty - auto-detect
        context: contextWithMessages,
      });
      
      // ✅ Ensure originalMessages is stored in each result
      return results.map(r => ({
        ...r,
        originalMessages: messageTexts,
      }));
    }, contextWithMessages);
  };

  // ✅ Use response (move to input + attach files)
  const handleUseResponse = (result) => {
    // Set text to input
    setNewMessage(result.text);
    
    // Attach files from result (if any)
    if (result.files && result.files.length > 0) {
      setAttachedFiles(prev => [...prev, ...result.files]);
      console.log('[ChatWindow] Attached files from AI response:', result.files.length, result.files);
    }
    
    inputRef.current?.focus();
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 100) + 'px';
      }
    }, 0);
  };

  // Regenerate translate result
  // ✅ Now uses originalText stored in the result
  const handleRegenerateTranslate = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.TRANSLATE) <= 0) return;
    
    setRegeneratingId(result.id);
    
    try {
      // ✅ Use original text from the result
      const originalText = result.originalText || result.text;
      
      await aiWorkspace.regenerateResult(
        result.id, 
        ACTION_TYPES.TRANSLATE,
        async (params) => {
          const results = await aiAgentService.processAction({
            ...params,
            text: originalText, // ✅ Use original text
          });
          
          // ✅ Preserve originalText in regenerated result
          return results.map(r => ({
            ...r,
            originalText: originalText,
          }));
        },
        { text: originalText, originalText }
      );
    } finally {
      setRegeneratingId(null);
    }
  };

  // Regenerate generate result
  // ✅ Now uses originalMessages stored in the result
  const handleRegenerateGenerate = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.GENERATE) <= 0) return;
    
    setRegeneratingId(result.id);
    
    try {
      // ✅ Use original messages from the result
      const originalMessages = result.originalMessages || [];
      
      if (originalMessages.length === 0) {
        console.warn('[ChatWindow] No original messages found for regeneration');
        return;
      }

      await aiWorkspace.regenerateResult(
        result.id, 
        ACTION_TYPES.GENERATE,
        async (params) => {
          const results = await aiAgentService.processAction({
            ...params,
            messages: originalMessages, // ✅ Use original messages
            context: {
              chatId: chat?.remoteJid,
              chatName: getChatName(),
            },
          });
          
          // ✅ Preserve originalMessages in regenerated result
          return results.map(r => ({
            ...r,
            originalMessages: originalMessages,
          }));
        },
        { originalMessages }
      );
    } finally {
      setRegeneratingId(null);
    }
  };

  // Execute CUSTOM prompt
  // Execute CUSTOM prompt
  // ✅ Now stores originalPrompt for regeneration
  const handleExecuteCustom = async (prompt) => {
    if (!prompt?.trim()) return;

    const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
    const originalPrompt = prompt.trim();
    
    await aiWorkspace.executeCustom(originalPrompt, async ({ action, prompt: userPrompt }) => {
      const results = await aiAgentService.processAction({
        action,
        prompt: userPrompt,
        context: {
          userId: userMetadata.id || 'anonymous',
          chatId: chat?.remoteJid,
          chatName: getChatName(),
        },
      });
      
      // ✅ Store originalPrompt in each result for regeneration
      return results.map(r => ({
        ...r,
        originalPrompt: originalPrompt,
      }));
    }, {
      userId: userMetadata.id || 'anonymous',
      chatId: chat?.remoteJid,
      chatName: getChatName(),
    });
  };

  // Regenerate custom result
  // ✅ Now uses originalPrompt stored in the result
  const handleRegenerateCustom = async (result) => {
    if (aiWorkspace.getRemainingRegenerations(result.id, ACTION_TYPES.CUSTOM) <= 0) return;
    
    setRegeneratingId(result.id);
    
    try {
      const userMetadata = JSON.parse(localStorage.getItem('userData') || '{}');
      
      // ✅ Use original prompt from the result
      const originalPrompt = result.originalPrompt || '';
      
      if (!originalPrompt) {
        console.warn('[ChatWindow] No original prompt found for regeneration');
        return;
      }
      
      await aiWorkspace.regenerateResult(
        result.id, 
        ACTION_TYPES.CUSTOM,
        async (params) => {
          const results = await aiAgentService.processAction({
            ...params,
            prompt: originalPrompt, // ✅ Use original prompt
            context: {
              userId: userMetadata.id || 'anonymous',
              chatId: chat?.remoteJid,
              chatName: getChatName(),
            },
          });
          
          // ✅ Preserve originalPrompt in regenerated result
          return results.map(r => ({
            ...r,
            originalPrompt: originalPrompt,
          }));
        },
        { originalPrompt }
      );
    } finally {
      setRegeneratingId(null);
    }
  };

  // Handle message selection with validation
  const handleToggleMessageSelection = (msgId, msg) => {
    const isBlocked = isMessageBlocked(msg);
    const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;
    const isCurrentlySelected = aiWorkspace.selectedMessages.has(msgId);
    
    if (isCurrentlySelected) {
      aiWorkspace.toggleMessageSelection(msgId, msg);
      return;
    }
    
    if (isBlocked || isAtLimit) {
      return;
    }
    
    aiWorkspace.toggleMessageSelection(msgId, msg);
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = formatDate(msg.messageTimestamp);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative',
    },
    emptyContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgSecondary,
    },
    emptyContent: {
      textAlign: 'center',
      color: colors.textSecondary,
    },
    emptyTitle: {
      fontSize: '32px',
      fontWeight: 300,
      color: colors.textPrimary,
      marginBottom: '12px',
    },
    emptyText: {
      fontSize: '14px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 16px',
      backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`,
      minHeight: '60px',
    },
    headerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      overflow: 'hidden',
    },
    avatarImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    avatarPlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    headerDetails: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerName: {
      fontWeight: 500,
      fontSize: '16px',
      color: colors.textPrimary,
    },
    headerStatus: {
      fontSize: '13px',
      color: colors.textSecondary,
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    headerActionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgChat,
    },
    messagesLoading: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    messagesEmpty: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    messageGroup: {
      marginBottom: '16px',
    },
    dateDivider: {
      display: 'flex',
      justifyContent: 'center',
      margin: '16px 0',
    },
    dateDividerText: {
      backgroundColor: colors.bgTertiary,
      padding: '6px 14px',
      borderRadius: '8px',
      fontSize: '12px',
      color: colors.textSecondary,
      boxShadow: colors.shadowSm,
    },
    messageRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginBottom: '2px',
      padding: '2px 0',
      borderRadius: '8px',
      transition: 'background-color 0.15s ease',
    },
    messageRowSelected: {
      backgroundColor: `${workspaceColors.accentPrimary}15`,
      margin: '2px -8px',
      padding: '2px 8px',
    },
    messageBubbleWrapper: {
      flex: 1,
      display: 'flex',
    },
    messageSent: {
      justifyContent: 'flex-end',
    },
    messageReceived: {
      justifyContent: 'flex-start',
    },
    messageContentSent: {
      maxWidth: '65%',
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: colors.messageSent,
      color: colors.messageSentText,
      borderTopRightRadius: '2px',
    },
    messageContentReceived: {
      maxWidth: '65%',
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: colors.messageReceived,
      color: colors.messageReceivedText,
      borderTopLeftRadius: '2px',
    },
    messageText: {
      fontSize: '14px',
      lineHeight: 1.4,
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
      margin: 0,
    },
    messageMeta: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '4px',
      marginTop: '4px',
    },
    messageTimeSent: {
      fontSize: '11px',
      color: colors.messageTimeSent,
    },
    messageTimeReceived: {
      fontSize: '11px',
      color: colors.textTertiary,
    },
    messageMedia: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '200px',
      height: '150px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      marginBottom: '8px',
      color: colors.textTertiary,
    },
    messageDocument: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      marginBottom: '8px',
    },
    scrollButton: {
      position: 'absolute',
      bottom: '80px',
      right: '24px',
      width: '42px',
      height: '42px',
      borderRadius: '50%',
      backgroundColor: colors.bgPrimary,
      border: 'none',
      boxShadow: colors.shadowMd,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
      zIndex: 10,
    },
    inputContainer: {
      backgroundColor: colors.headerBg,
    },
    inputForm: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: '8px',
      padding: '12px 16px',
    },
    inputActionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '42px',
      height: '42px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
    },
    inputWrapper: {
      flex: 1,
      backgroundColor: colors.inputBg,
      borderRadius: '8px',
      border: `1px solid ${colors.inputBorder}`,
    },
    textarea: {
      width: '100%',
      border: 'none',
      background: 'transparent',
      padding: '12px 16px',
      fontSize: '15px',
      color: colors.textPrimary,
      resize: 'none',
      outline: 'none',
      maxHeight: '100px',
      lineHeight: 1.4,
      fontFamily: 'inherit',
    },
    sendBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '42px',
      height: '42px',
      border: 'none',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      borderRadius: '50%',
      cursor: 'pointer',
      flexShrink: 0,
    },
    sendBtnDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };

  // ============================================
  // RENDER
  // ============================================

  if (!chat) {
    return (
      <div style={styles.emptyContainer}>
        <div style={styles.emptyContent}>
          <h3 style={styles.emptyTitle}>MinistrareIA</h3>
          <p style={styles.emptyText}>Selecione uma conversa para começar a enviar mensagens</p>
        </div>
      </div>
    );
  }

  const canSend = (newMessage.trim() || attachedFiles.length > 0) && !uploading;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerInfo}>
          <div style={styles.headerAvatar}>{getChatAvatar()}</div>
          <div style={styles.headerDetails}>
            <span style={styles.headerName}>{getChatName()}</span>
            <span style={styles.headerStatus}>
              {chat._isGroup || whatsappService.isGroup(chat.remoteJid)
                ? 'Grupo'
                : chatUtils.formatPhoneNumber(chat._phoneNumber) || whatsappService.extractNumber(chat.remoteJid)}
            </span>
          </div>
        </div>
        <div style={styles.headerActions}>
          {/* Autopilot activate / block buttons */}
          {(() => {
            const chatPhone = chat._phoneNumber || whatsappService.extractNumber(chat.remoteJid);
            const isAutopilotOn = (autopilotContacts || []).includes(chatPhone);
            const isBlocked = (blockedContacts || []).some(c =>
              c.replace(/\D/g, '') === chatPhone?.replace(/\D/g, '')
            );
            return (
              <>
                {/* Activate / Deactivate agent button */}
                <button
                  onClick={() => onToggleAutopilot?.(chatPhone, !isAutopilotOn)}
                  disabled={isBlocked}
                  style={{
                    ...styles.headerActionBtn,
                    backgroundColor: isAutopilotOn ? `${colors.accentPrimary}15` : 'transparent',
                    border: `1.5px solid ${isAutopilotOn ? colors.accentPrimary : colors.borderColor}`,
                    borderRadius: '20px',
                    width: 'auto',
                    padding: '5px 10px',
                    gap: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: isBlocked ? 0.4 : 1,
                    cursor: isBlocked ? 'not-allowed' : 'pointer',
                  }}
                  title={isBlocked ? 'Desbloqueie o contato primeiro' : isAutopilotOn ? 'Desativar agente para este contato' : 'Ativar agente para este contato'}
                >
                  <Bot size={14} color={isAutopilotOn ? colors.accentPrimary : colors.textSecondary} />
                  <span style={{ fontSize: '11px', fontWeight: 500, color: isAutopilotOn ? colors.accentPrimary : colors.textSecondary }}>
                    {isAutopilotOn ? 'Ativo' : 'Ativar'}
                  </span>
                </button>
                {/* Block / Unblock agent button */}
                <button
                  onClick={() => onToggleBlockedContact?.(chatPhone, !isBlocked)}
                  style={{
                    ...styles.headerActionBtn,
                    backgroundColor: isBlocked ? '#EF444415' : 'transparent',
                    border: `1.5px solid ${isBlocked ? '#EF4444' : colors.borderColor}`,
                    borderRadius: '20px',
                    width: 'auto',
                    padding: '5px 10px',
                    gap: '5px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  title={isBlocked ? 'Desbloquear agente para este contato' : 'Bloquear agente para este contato'}
                >
                  <ShieldOff size={14} color={isBlocked ? '#EF4444' : colors.textSecondary} />
                  <span style={{ fontSize: '11px', fontWeight: 500, color: isBlocked ? '#EF4444' : colors.textSecondary }}>
                    {isBlocked ? 'Bloqueado' : 'Bloquear'}
                  </span>
                </button>
              </>
            );
          })()}
          <button style={styles.headerActionBtn} title="Mais opções">
            <MoreVertical size={20} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        style={styles.messagesContainer}
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {loading ? (
          <div style={styles.messagesLoading}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '12px' }} />
            <span>Carregando mensagens...</span>
          </div>
        ) : messages.length === 0 ? (
          <div style={styles.messagesEmpty}>
            <p style={{ fontSize: '16px', marginBottom: '4px' }}>Nenhuma mensagem ainda</p>
            <span style={{ fontSize: '14px', color: colors.textTertiary }}>
              Envie uma mensagem para iniciar a conversa
            </span>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date} style={styles.messageGroup}>
              <div style={styles.dateDivider}>
                <span style={styles.dateDividerText}>{date}</span>
              </div>
              {msgs.map((msg, index) => {
                const isSent = msg.key?.fromMe;
                const msgId = msg.id || msg.key?.id || `msg-${index}`;
                const isSelected = aiWorkspace.selectedMessages.has(msgId);
                const isHovered = hoveredMessage === msgId;
                const showCheckbox = isHovered || isSelected || aiWorkspace.selectedMessages.size > 0;
                const msgIsBlocked = isMessageBlocked(msg);
                const isAtLimit = aiWorkspace.selectedMessages.size >= MAX_SELECTED_MESSAGES;

                return (
                  <div
                    key={msgId}
                    style={{
                      ...styles.messageRow,
                      ...(isSelected ? styles.messageRowSelected : {}),
                    }}
                    onMouseEnter={() => setHoveredMessage(msgId)}
                    onMouseLeave={() => setHoveredMessage(null)}
                  >
                    {/* Checkbox for selection */}
                    <MessageCheckbox
                      isSelected={isSelected}
                      isVisible={showCheckbox}
                      onToggle={() => handleToggleMessageSelection(msgId, msg)}
                      isBlocked={msgIsBlocked}
                      isAtLimit={isAtLimit && !isSelected}
                      blockReason={msgIsBlocked ? 'Apenas mensagens de texto' : ''}
                    />

                    {/* Message bubble */}
                    <div 
                      style={{
                        ...styles.messageBubbleWrapper,
                        ...(isSent ? styles.messageSent : styles.messageReceived),
                      }}
                    >
                      <div style={isSent ? styles.messageContentSent : styles.messageContentReceived}>
                        {/* Media Content (images, audio, video, documents) */}
                        {hasMediaContent(msg) && (
                          <MessageMedia
                            message={msg}
                            isSent={isSent}
                            onDownload={handleDownloadMedia}
                          />
                        )}
                        
                        {/* Text Content (only show if text-only or has caption) */}
                        {isTextOnly(msg) && (
                          <p style={styles.messageText}>{getMessageContent(msg)}</p>
                        )}
                        
                        <div style={styles.messageMeta}>
                          <span style={isSent ? styles.messageTimeSent : styles.messageTimeReceived}>
                            {formatTime(msg.messageTimestamp)}
                          </span>
                          {isSent && getMessageStatusIcon(msg.status)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll Button */}
      {showScrollButton && (
        <button style={styles.scrollButton} onClick={scrollToBottom}>
          <ArrowDown size={20} />
        </button>
      )}

      {/* AI Workspace */}
      <AIWorkspace
        isOpen={aiWorkspace.isWorkspaceOpen}
        onToggleWorkspace={aiWorkspace.toggleWorkspace}
        selectedCount={aiWorkspace.selectedMessages.size}
        inputText={newMessage}
        
        // Dropdown
        activeDropdown={aiWorkspace.activeDropdown}
        onToggleDropdown={aiWorkspace.toggleDropdown}
        onCloseDropdown={aiWorkspace.closeDropdown}
        
        // Translate
        translateLanguages={aiWorkspace.translateLanguages}
        onToggleTranslateLanguage={aiWorkspace.toggleTranslateLanguage}
        translateResults={aiWorkspace.translateResults}
        translateProcessing={aiWorkspace.translateProcessing}
        translateError={aiWorkspace.translateError}
        hasTranslateResults={aiWorkspace.hasTranslateResults}
        
        // Generate (no longer needs language selection)
        generateResults={aiWorkspace.generateResults}
        generateProcessing={aiWorkspace.generateProcessing}
        generateError={aiWorkspace.generateError}
        hasGenerateResults={aiWorkspace.hasGenerateResults}
        
        // Custom Prompt
        customResults={aiWorkspace.customResults}
        customProcessing={aiWorkspace.customProcessing}
        customError={aiWorkspace.customError}
        hasCustomResults={aiWorkspace.hasCustomResults}
        
        // ✅ History - using integrated aiWorkspace
        historyItems={aiWorkspace.historyItems}
        isHistoryOpen={aiWorkspace.isHistoryOpen}
        onToggleHistory={aiWorkspace.toggleHistory}
        onRemoveFromHistory={aiWorkspace.removeFromHistory}
        onClearHistory={aiWorkspace.clearHistory}
        hasHistory={aiWorkspace.hasHistory}
        
        // Actions
        onClear={aiWorkspace.clearSelection}
        onExecuteTranslate={handleExecuteTranslate}
        onExecuteGenerate={handleExecuteGenerate}
        onExecuteCustom={handleExecuteCustom}
        onUseResponse={handleUseResponse}
        onRegenerateTranslate={handleRegenerateTranslate}
        onRegenerateGenerate={handleRegenerateGenerate}
        onRegenerateCustom={handleRegenerateCustom}
        onRemoveTranslateResult={aiWorkspace.removeTranslateResult}
        onRemoveGenerateResult={aiWorkspace.removeGenerateResult}
        onRemoveCustomResult={aiWorkspace.removeCustomResult}
        
        // Regeneration
        getTranslateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.TRANSLATE)}
        getGenerateRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.GENERATE)}
        getCustomRemainingRegenerations={(id) => aiWorkspace.getRemainingRegenerations(id, ACTION_TYPES.CUSTOM)}
        regeneratingId={regeneratingId}
      />

      {/* Input Area */}
      <div style={styles.inputContainer}>
        {/* Upload Error Message */}
        {uploadError && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: `${colors.danger || '#ef4444'}15`,
            borderTop: `1px solid ${colors.danger || '#ef4444'}30`,
            color: colors.danger || '#ef4444',
            fontSize: '12px',
          }}>
            ⚠️ {uploadError}
          </div>
        )}
        
        {/* Attached Files Preview */}
        <AttachedFilesPreview
          files={attachedFiles}
          onRemoveFile={handleRemoveAttachedFile}
          onClearAll={handleClearAttachedFiles}
        />
        
        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={fileUploadService.getAcceptString()}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {/* Input Form */}
        <form onSubmit={handleSend} style={styles.inputForm}>
          <button 
            type="button" 
            style={{
              ...styles.inputActionBtn,
              ...(uploading ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
            }}
            title={uploading ? 'Enviando arquivos...' : 'Anexar arquivo'}
            onClick={handleAttachClick}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Paperclip size={24} />
            )}
          </button>
          <div style={styles.inputWrapper}>
            <textarea
              ref={inputRef}
              placeholder="Digite uma mensagem"
              value={newMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              rows={1}
              disabled={sending}
              style={styles.textarea}
            />
          </div>
          <button
            type="submit"
            style={{
              ...styles.sendBtn,
              ...(!canSend || sending ? styles.sendBtnDisabled : {}),
            }}
            disabled={!canSend || sending}
          >
            {sending ? (
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Send size={20} />
            )}
          </button>
        </form>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ChatWindow;